// src/hooks/useAnalytics.ts
// Fetches aggregated analytics data from the backend for the dashboard charts.

import { useCallback, useEffect, useState } from "react";
import { fetchData } from "api/apiHelpers";
import logger from "utils/logger";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DomainCount {
  domain: string;
  count: number;
}

export interface CohortSummary {
  total_patients: number;
  new_this_month: number;
  total_documents: number;
  domains: DomainCount[];
}

export interface AgeBin {
  label: string;
  count: number;
}

export interface GenderCount {
  label: string;
  count: number;
}

export interface DemographicsData {
  age_bins: AgeBin[];
  gender: GenderCount[];
}

export interface StagingNode {
  name: string;
  value?: number;
  children?: StagingNode[];
}

export interface StagingData {
  children: StagingNode[];
}

export interface MutationRow {
  gene: string;
  positive: number;
  negative: number;
  unknown: number;
}

export interface MutationsData {
  mutations: MutationRow[];
}

export interface BiomarkerRow {
  name: string;
  value: number;
  count: number;
}

export interface BiomarkersData {
  biomarkers: BiomarkerRow[];
}

export interface TherapyItem {
  name: string;
  value: number;
}

export interface TreatmentsData {
  therapy_types: TherapyItem[];
  therapy_responses: TherapyItem[];
}

export interface TimelineSeries {
  name: string;
  data: number[];
}

export interface EventsTimelineData {
  months: string[];
  series: TimelineSeries[];
}

export interface CompletenessRow {
  table: string;
  completeness: number;
  records: number;
}

export interface CompletenessData {
  tables: CompletenessRow[];
}

export interface AnalyticsState {
  cohort: CohortSummary | null;
  demographics: DemographicsData | null;
  staging: StagingData | null;
  mutations: MutationsData | null;
  biomarkers: BiomarkersData | null;
  treatments: TreatmentsData | null;
  timeline: EventsTimelineData | null;
  completeness: CompletenessData | null;
  loading: boolean;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const useAnalytics = (environmentIds?: number[]) => {
  const [state, setState] = useState<AnalyticsState>({
    cohort: null,
    demographics: null,
    staging: null,
    mutations: null,
    biomarkers: null,
    treatments: null,
    timeline: null,
    completeness: null,
    loading: true,
    error: null,
  });

  // Build query string for environment filtering
  const envQs = environmentIds && environmentIds.length > 0
    ? `?environments=${environmentIds.join(",")}`
    : "";

  const fetchAll = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    // Use Promise.allSettled so a single failing endpoint does not wipe
    // all analytics to zero.  Each endpoint resolves independently.
    const [cohortRes, demoRes, stgRes, mutRes, bioRes, treatRes, tlRes, compRes] =
      await Promise.allSettled([
        fetchData<CohortSummary>(`/insights/cohort-summary/${envQs}`),
        fetchData<DemographicsData>(`/insights/demographics/${envQs}`),
        fetchData<StagingData>(`/insights/staging/${envQs}`),
        fetchData<MutationsData>(`/insights/mutations/${envQs}`),
        fetchData<BiomarkersData>(`/insights/biomarkers/${envQs}`),
        fetchData<TreatmentsData>(`/insights/treatments/${envQs}`),
        fetchData<EventsTimelineData>(`/insights/events-timeline/${envQs}`),
        fetchData<CompletenessData>(`/insights/completeness/${envQs}`),
      ]);

    const val = <T,>(r: PromiseSettledResult<T>, fallback: T): T =>
      r.status === "fulfilled" ? r.value : fallback;

    const results = [cohortRes, demoRes, stgRes, mutRes, bioRes, treatRes, tlRes, compRes];
    const failCount = results.filter((r) => r.status === "rejected").length;

    if (failCount > 0) {
      logger.warn(
        `Analytics: ${failCount} endpoint(s) failed`,
        results
          .filter((r) => r.status === "rejected")
          .map((r) => (r as PromiseRejectedResult).reason),
      );
    }

    setState({
      cohort: val(cohortRes, { total_patients: 0, new_this_month: 0, total_documents: 0, domains: [] }),
      demographics: val(demoRes, { age_bins: [], gender: [] }),
      staging: val(stgRes, { children: [] }),
      mutations: val(mutRes, { mutations: [] }),
      biomarkers: val(bioRes, { biomarkers: [] }),
      treatments: val(treatRes, { therapy_types: [], therapy_responses: [] }),
      timeline: val(tlRes, { months: [], series: [] }),
      completeness: val(compRes, { tables: [] }),
      loading: false,
      error: failCount > 0
        ? `Some analytics data could not be loaded (${failCount} endpoint${failCount > 1 ? "s" : ""} failed).`
        : null,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envQs]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { ...state, refetch: fetchAll };
};

export default useAnalytics;
